import { gql, useMutation, useQuery } from '@apollo/client';
import { capitalize } from 'lodash';
import { useState } from 'react';
import { Button, Label, ModalBody } from 'reactstrap';
import Modal from './basics/Modal';
import { FARM_ROLES } from '../constants/teamRoles';
import {
  RemoveFarmMemberMutation,
  ShareFarmMutation,
  UpsertFarmMembersMutation,
} from '../mutations';
import { getFirstQueryResult } from '../utils/graphql';
import { useFarmCanBeEdited } from '../utils/hooks';
import DropdownButton from './basics/DropdownButton';
import EmailForm from './EmailForm';
import SwitchInput from './basics/SwitchInput';
import { getFarmPath } from '../utils/utils';
import { captureEvent } from '../utils/analytics';
import LinkIcon from './icons/LinkIcon';
import IconContainer from './basics/IconContainer';
import useMutateWithState from '../hooks/useMutateWithState';

const farmUsersAndTeamQuery = gql`
  query Farm($farmId: ID!) {
    farm(farmId: $farmId) {
      id
      name
      isShared
      shareId
      updatedAt
      members {
        id
        email
        isPending
        firstName
        lastName
        role
      }
      team {
        id
        name
      }
    }
  }
`;

const COPY_LINK = 'Copy link';
const LINK_COPIED = 'Link copied!';

const ShareFarmEmailForm = ({ farm, mutateAndSetState, loading }) => {
  const [mutation] = useMutation(UpsertFarmMembersMutation, {
    refetchQueries: [
      {
        query: farmUsersAndTeamQuery,
        variables: { farmId: farm?.id },
      },
    ],
  });

  return (
    <EmailForm
      loading={loading}
      onSubmit={(values, { resetForm }) =>
        mutateAndSetState(() => {
          return mutation({
            variables: {
              input: {
                updatedAt: farm?.updatedAt,
                farmId: farm?.id,
                members: values.emails,
              },
            },
          }).then((result) => {
            resetForm();
            return result;
          });
        }, 'Access updated!')
      }
    />
  );
};

const RoleDropdownButton = ({ farm, member, loading, mutateAndSetState }) => {
  const [upsertFarmMembers] = useMutation(UpsertFarmMembersMutation, {
    refetchQueries: [
      {
        query: farmUsersAndTeamQuery,
        variables: { farmId: farm?.id },
      },
    ],
  });

  const { id: farmId, updatedAt } = farm;
  const { id: memberId, role } = member;

  const onSetOption = (selectedRole) => {
    mutateAndSetState(
      () =>
        upsertFarmMembers({
          variables: {
            input: {
              farmId,
              updatedAt,
              members: [
                {
                  id: memberId,
                  role: selectedRole.value,
                },
              ],
            },
          },
        }),
      'Access updated!',
    );
  };

  return (
    <DropdownButton
      inModal
      disabled={loading}
      color="link"
      options={FARM_ROLES}
      selectedOption={capitalize(role)}
      setOption={onSetOption}
    />
  );
};

const ShareFarmButton = ({ farmId, ...buttonProps }) => {
  const { data: farmData } = useQuery(farmUsersAndTeamQuery, {
    fetchPolicy: 'cache-and-network',
    context: { priorityOp: true },
    variables: { farmId },
  });
  const [modalOpen, setModalOpen] = useState(false);

  const farm = getFirstQueryResult(farmData);
  const farmTeam = farm?.team?.name;
  const members = farm?.members || [];

  const [{ loading }, mutateAndSetState] = useMutateWithState();

  const [deleteMutation] = useMutation(RemoveFarmMemberMutation, {
    refetchQueries: [{ query: farmUsersAndTeamQuery, variables: { farmId } }],
  });
  const [shareFarmMutation] = useMutation(ShareFarmMutation, {
    refetchQueries: [{ query: farmUsersAndTeamQuery, variables: { farmId } }],
  });

  const farmCanBeEdited = useFarmCanBeEdited(farmId);
  const farmPath = `${getFarmPath(farmId, 'fields')}?sid=${farm?.shareId}`;
  const [buttonText, setButtonText] = useState(COPY_LINK);

  return (
    <>
      <Button type="button" onClick={() => setModalOpen(true)} {...buttonProps}>
        Share
      </Button>
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
        <ModalBody>
          <div className="p-3">
            <div className="d-flex justify-content-between align-items-center font-xl">
              <div>Share Project</div>
              <Button
                onClick={() => setModalOpen(false)}
                color="link"
                className="p-0"
              >
                ×
              </Button>
            </div>
            {farmCanBeEdited && (
              <ShareFarmEmailForm
                farm={farm}
                mutateAndSetState={mutateAndSetState}
                loading={loading}
              />
            )}
            <div className="text-muted py-3">
              {farmTeam
                ? `Everyone at ${farmTeam} has access to view or edit ${farm?.name}`
                : `${farm?.name} is not visible to a team. See farm access permissions below`}
            </div>
            <div className="font-weight-bold">Other Farm Members</div>
            {members.length ? (
              <div className="py-2">
                {members.map((member) => (
                  <div
                    key={member.email}
                    className="d-flex justify-content-between align-items-md-center flex-md-row flex-column my-2"
                  >
                    <div className="d-flex flex-column">
                      {member.firstName != null && member.firstName !== '' ? (
                        <>
                          <div className="my-1">{`${member.firstName} ${
                            member.lastName || ''
                          }`}</div>
                          <div className="text-muted">{member.email}</div>
                        </>
                      ) : (
                        member.email
                      )}
                    </div>
                    <div className="d-flex">
                      {farmCanBeEdited ? (
                        <>
                          <RoleDropdownButton
                            farm={farm}
                            mutateAndSetState={mutateAndSetState}
                            loading={loading}
                            member={member}
                          />
                          <Button
                            type="button"
                            color="link"
                            size="sm"
                            onClick={() =>
                              mutateAndSetState(
                                () =>
                                  deleteMutation({
                                    variables: {
                                      input: {
                                        farmId: farm?.id,
                                        userId: member.id,
                                        updatedAt: farm?.updatedAt,
                                      },
                                    },
                                  }),
                                'Access updated!',
                              )
                            }
                          >
                            Remove
                          </Button>
                        </>
                      ) : (
                        <div className="text-muted">
                          {capitalize(member.role)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-muted py-2">
                No one has access to view or edit {farm?.name}
              </div>
            )}
            <div className="font-weight-bold">General Access</div>
            <div className="pt-2">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <Label className="d-flex align-items-center text-muted m-0 mr-2">
                    Anyone with the link can view
                  </Label>
                  <SwitchInput
                    checked={farm?.isShared}
                    className="py-0"
                    disabled={loading || !farmCanBeEdited}
                    onChange={(e) => {
                      if (!farmCanBeEdited) return;
                      captureEvent('farm-is-shared-updated', {
                        location: 'share modal',
                      });
                      mutateAndSetState(() =>
                        shareFarmMutation({
                          variables: {
                            input: {
                              farmId: farm?.id,
                              isShared: e.target.checked,
                              updatedAt: farm?.updatedAt,
                            },
                          },
                        }),
                      );
                    }}
                  />
                </div>
                {farm?.isShared && (
                  <Button
                    size="sm"
                    className="py-0"
                    color="link"
                    disabled={buttonText === LINK_COPIED}
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `${window.location.origin}${farmPath}`,
                      );
                      // Change button text, then reset it after 5 seconds
                      setButtonText(LINK_COPIED);
                      setTimeout(() => setButtonText(COPY_LINK), 5000);
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <IconContainer iconSize="16px" className="mr-1">
                        <LinkIcon />
                      </IconContainer>
                      {buttonText}
                    </div>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default ShareFarmButton;
